import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AdminCities() {
  const [items, setItems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ country_id: "", name: "", status: "active" });
  const [tab, setTab] = useState("");

  const load = () => api.get("/admin/cities", { params: tab ? { status: tab } : {} }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [tab]);
  useEffect(() => { api.get("/admin/countries").then((r) => setCountries(r.data)); }, []);

  const save = async () => {
    try { await api.post("/admin/cities", form); toast.success("City added"); setOpen(false); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const approve = async (id) => { await api.post(`/admin/cities/${id}/approve`); toast.success("Approved"); load(); };
  const del = async (id) => { if (window.confirm("Delete city?")) { await api.delete(`/admin/cities/${id}`); load(); } };

  const pending = items.filter((c) => c.status === "pending").length;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Cities</h1>
        <Button className="bg-blue-800 hover:bg-blue-900" onClick={() => { setForm({ country_id: countries[0]?.id || "", name: "", status: "active" }); setOpen(true); }} data-testid="add-city-btn"><Plus className="mr-1.5 h-4 w-4" />Add City</Button>
      </div>
      <div className="mb-3 flex gap-2">
        {[["", "All"], ["pending", `Pending${pending ? ` (${pending})` : ""}`], ["active", "Active"]].map(([v, l]) => (
          <Button key={v} size="sm" variant={tab === v ? "default" : "outline"} className={tab === v ? "bg-blue-800" : ""} onClick={() => setTab(v)}>{l}</Button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[500px] text-left text-sm">
          <thead className="bg-slate-50 text-slate-500"><tr><th className="p-3">City</th><th className="p-3">Country</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100">
                <td className="p-3 font-medium text-slate-900">{c.name}</td>
                <td className="p-3 text-slate-500">{c.country}</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs capitalize ${c.status === "active" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>{c.status}</span></td>
                <td className="p-3"><div className="flex gap-1">{c.status === "pending" && <Button size="icon" variant="ghost" onClick={() => approve(c.id)} data-testid={`approve-city-${c.id}`}><Check className="h-4 w-4 text-green-600" /></Button>}<Button size="icon" variant="ghost" onClick={() => del(c.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add City</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Country</Label>
              <Select value={form.country_id} onValueChange={(v) => setForm((f) => ({ ...f, country_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="max-h-60">{countries.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>City Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="city-name" /></div>
          </div>
          <DialogFooter><Button className="bg-blue-800 hover:bg-blue-900" onClick={save} data-testid="save-city-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
